import { formatMonthlyLabel } from './dateHelpers';

interface SelectedEntity {
  id: number;
  name: string;
}

// Helper function to parse data keys and handle entity selection
export const parseDataKey = (key: string, selectedEntities: SelectedEntity[]) => {
  let monthLabel: string;
  let entityId: string;

  if (key.includes(' - ') && key.includes('202')) {
    // Quarterly data: "Oct - Dec 2024"
    monthLabel = key;
    entityId = key.split('_')[0] || '';
  } else {
    // Monthly data: "entity_year_month_type"
    const [entity_id, year, month] = key.split('_');
    monthLabel = month && year ? formatMonthlyLabel(parseInt(month), parseInt(year)) : key;
    entityId = entity_id;
  }

  const isAllSelected = selectedEntities?.some((entity: SelectedEntity) => entity.name === 'All');
  const isEntitySelected = selectedEntities?.some(
    (entity: SelectedEntity) => entity.id.toString() === entityId
  );

  return {
    monthLabel,
    entityId,
    isAllSelected,
    isEntitySelected,
    shouldInclude: !selectedEntities || isAllSelected || isEntitySelected
  };
};

// Helper function to aggregate data for quarters
export const aggregateQuarterData = (existing: any, newData: any): any => {
  // If existing data is reportNotFound, replace with new data
  if (existing.data && (existing.data as any).reportNotFound) {
    return JSON.parse(JSON.stringify(newData));
  }

  // If new data is reportNotFound, keep existing data
  if (newData.data && (newData.data as any).reportNotFound) {
    return existing;
  }

  // If both are arrays, concatenate
  if (Array.isArray(existing.data) && Array.isArray(newData.data)) {
    const result = JSON.parse(JSON.stringify(existing));
    result.data = existing.data.concat(newData.data);
    return result;
  }

  // If existing is object and new is array, replace with array
  if (
    typeof existing.data === 'object' &&
    !Array.isArray(existing.data) &&
    Array.isArray(newData.data)
  ) {
    return JSON.parse(JSON.stringify(newData));
  }

  // If both are objects, sum numeric properties
  if (
    typeof existing.data === 'object' &&
    typeof newData.data === 'object' &&
    !Array.isArray(existing.data) &&
    !Array.isArray(newData.data)
  ) {
    const result = JSON.parse(JSON.stringify(existing));
    const src = newData.data as Record<string, number>;
    const dest = result.data as Record<string, number>;

    for (const prop in src) {
      if (Object.prototype.hasOwnProperty.call(src, prop) && typeof src[prop] === 'number') {
        dest[prop] = (dest[prop] || 0) + src[prop];
      }
    }
    return result;
  }

  // Default: keep existing data
  return existing;
};
